import { Fragment, PureComponent } from 'react'
import PropTypes from 'prop-types'

import { Button, Column, Form, Input, Link, Modal, Video } from '../../../..'
import { mediaForm as mediaFormProps } from '../../../../../types'
import { DEFAULT_IMAGE_LENGTH } from '../../../../../reducers/content/initialState'

import ImagePreview from './ImagePreview'

const { FormGroup, FormHeader, Label } = Form

class MediaForm extends PureComponent {
    static propTypes = {
        errors: PropTypes.shape({
            name: PropTypes.string,
            file: PropTypes.string,
            date_start: PropTypes.string,
            date_end: PropTypes.string,
            length: PropTypes.string,
        }),
        isOpen: PropTypes.bool,
        isWorking: PropTypes.bool,
        values: PropTypes.shape(mediaFormProps),
        onChangeForm: PropTypes.shape({
            name: PropTypes.func,
            file: PropTypes.func,
            date_start: PropTypes.func,
            date_end: PropTypes.func,
            metadata: PropTypes.func,
        }),
        onClickCancel: PropTypes.func,
        onClickSubmit: PropTypes.func,
        onClickNullStartDate: PropTypes.func.isRequired,
        onClickNullEndDate: PropTypes.func.isRequired,
        onClickRemoveStartDate: PropTypes.func.isRequired,
        onClickRemoveEndDate: PropTypes.func.isRequired,
        progress: PropTypes.number.isRequired,
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        errors: {
            name: null,
            file: null,
            date_start: null,
            date_end: null,
        },
        isOpen: false,
        isWorking: false,
        values: {},
        onChangeForm: {
            name: () => null,
            media: () => null,
            date_start: () => null,
            date_end: () => null,
            length: () => null,
        },
        onClickCancel: () => null,
        onClickSubmit: () => null,
    }

    /**
     * If a file has been selected to be upload
     */
    get hasFile() {
        const { values } = this.props
        const { file } = values || {}
        return !!file.length && file[0] instanceof File
    }

    /**
     * If the selected media is an image
     */
    get isFileImage() {
        const { type = '' } = this.file || {}
        return type.includes('image')
    }

    /**
     * If the selected media is a video
     */
    get isFileVideo() {
        const { type = '' } = this.file || {}
        return type.includes('video')
    }

    /**
     * Get file object for selected media to upload
     */
    get file() {
        const { values } = this.props
        const { file } = values || {}
        return file[0]
    }

    /**
     * Progress in 100 per cent
     */
    get progress() {
        const { progress } = this.props
        return Math.round(progress * 100)
    }

    /**
     * Video length needs to be calculated after rendering the video in html,
     * so this function is triggered when the <video> tag finishes loading the metadata
     */
    onVideoMetadataChange = (e) => {
        if (!this.hasFile || !this.isFileVideo) {
            return null
        }

        // Get metadata
        const {
            target: { videoHeight, videoWidth, duration },
        } = e

        const { onChangeForm } = this.props
        const { metadata: onChangeMetadata } = onChangeForm
        const videoLength = duration * 1000 // Set to ms

        const metadata = { videoHeight, videoLength, videoWidth }

        // If onChangeMetadata function exists
        if (onChangeMetadata instanceof Function) {
            onChangeMetadata(metadata)
        }
    }

    /**
     * If the file selected to upload is an image, we should trigger the onChangeLength event
     * with the default length
     */
    innerOnChangeFile = (...args) => {
        const { onChangeForm } = this.props
        const { length: onChangeLength, file: onChangeFile } = onChangeForm

        if (onChangeFile instanceof Function) {
            onChangeFile(...args)
        }

        // If file has been set, and it's an image, send the default image length
        if (this.hasFile && this.isFileImage && onChangeLength instanceof Function) {
            onChangeLength({
                target: { name: 'length', value: DEFAULT_IMAGE_LENGTH },
            })
        }
    }

    /**
     * Render Start Date label
     */
    renderStartDateLabel() {
        const { onClickRemoveStartDate, t } = this.props

        return (
            <Fragment>
                {t('date-start')}
                &nbsp;
                <Link onClick={onClickRemoveStartDate}>&times;</Link>
            </Fragment>
        )
    }

    /**
     * Render End label
     */
    renderEndDateLabel() {
        const { onClickRemoveEndDate, t } = this.props

        return (
            <Fragment>
                {t('date-end')}
                &nbsp;
                <Link onClick={onClickRemoveEndDate}>&times;</Link>
            </Fragment>
        )
    }

    /**
     * Render
     */
    render() {
        const { errors, isOpen, isWorking, onChangeForm, onClickCancel, onClickSubmit, onClickNullStartDate, onClickNullEndDate, values, t } = this.props
        const { name: errorName, file: errorFile, date_start: errorDateStart, date_end: errorDateEnd, length: errorLength } = errors
        const { file, name, date_start, date_end, length } = values
        const { name: onChangeName, date_start: onChangeDateStart, date_end: onChangeDateEnd, length: onChangeLength } = onChangeForm

        return (
            <Modal isOpen={isOpen} onClose={onClickCancel}>
                <Form>
                    <FormHeader>{t('Media upload')}</FormHeader>
                    <FormGroup title={t('Media')}>
                        <Label id="name" label={t('File Name')} error={errorName}>
                            <Input id="name" name="name" type="text" error={!!errorName} onChange={onChangeName} size="md" value={name} />
                        </Label>
                        <Label id="file" label="Media" error={errorFile}>
                            <Input
                                id="file"
                                name="file"
                                type="file"
                                error={!!errorFile}
                                onChange={this.innerOnChangeFile}
                                value={file}
                                accept="video/*,image/*,audio/*"
                            />
                        </Label>
                        {this.hasFile && this.isFileImage && (
                            <Label id="length" label={t('Length')} error={errorLength}>
                                <Input
                                    id="length"
                                    name="length"
                                    type="number"
                                    error={!!errorLength}
                                    min={0}
                                    onChange={onChangeLength}
                                    size="md"
                                    value={length}
                                />
                            </Label>
                        )}
                    </FormGroup>
                    {this.hasFile && (
                        <FormGroup maxHeight="260px" title={t('Preview')}>
                            {this.isFileImage && <ImagePreview src={this.file} />}
                            {this.isFileVideo && <Video onLoadedMetadata={this.onVideoMetadataChange} src={this.file} type={this.file.type} />}
                        </FormGroup>
                    )}
                    <FormGroup title={t('schedule')}>
                        {date_start && (
                            <Label id="date_start" label={this.renderStartDateLabel()} error={errorDateStart}>
                                <Input
                                    id="date_start"
                                    name="date_start"
                                    type="date"
                                    error={!!errorDateStart}
                                    onChange={onChangeDateStart}
                                    value={date_start}
                                    withTime
                                />
                            </Label>
                        )}
                        {!date_start && (
                            <div>
                                <Link onClick={onClickNullStartDate}>{t('date-start-add')}</Link>
                            </div>
                        )}
                        {date_end && (
                            <Label id="date_end" label={this.renderEndDateLabel()} error={errorDateEnd}>
                                <Input id="date_end" name="date_end" type="date" error={!!errorDateEnd} onChange={onChangeDateEnd} value={date_end} withTime />
                            </Label>
                        )}
                        {!date_end && (
                            <div>
                                <Link onClick={onClickNullEndDate}>{t('date-end-add')}</Link>
                            </div>
                        )}
                    </FormGroup>
                    <FormGroup>
                        <Column right width="100%">
                            {!!isWorking && (
                                <Button theme="light" onClick={onClickCancel} size="sm">
                                    {t('Cancel')}
                                </Button>
                            )}
                            <Button onClick={onClickSubmit} size="sm" theme={isWorking ? 'loading' : 'primary'} disabled={isWorking}>
                                {isWorking ? `${this.progress} %` : t('Upload')}
                            </Button>
                        </Column>
                    </FormGroup>
                </Form>
            </Modal>
        )
    }
}

export default MediaForm
